import { useEffect, useRef, useState } from "react";
import { StopData } from "../ZoneTravel/StopTravelBody";
import { useFetch } from "../../services/Requests/useFetch";
import { useRobotGPS } from "../../services/Ros/useRobotGPS";
import { useRobotTF } from "../../services/Ros/UseRobotTF";
import { useRobotDetails } from "../../utilities/RobotDetailsContext";

interface GPSPoint {
    lat: number;
    long: number;
}

const options: RequestInit = {
    method: "GET"
};

export const getDistance = (point1: GPSPoint, point2: GPSPoint,indoor:boolean) => {
    let distance: number;
    if (!indoor){
        const toRadians = (deg: number) => (deg * Math.PI) / 180;
        const R = 6371;
        const dLat = toRadians(point2.lat - point1.lat);
        const dLong = toRadians(point2.long - point1.long);
        
        const a = Math.sin(dLat / 2) ** 2 +
                Math.cos(toRadians(point1.lat)) * Math.cos(toRadians(point2.lat)) *
                Math.sin(dLong / 2) ** 2;
        
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        distance = R*c;
    }else{
        distance = Math.sqrt(Math.pow(point2.lat - point1.lat, 2) + Math.pow(point2.long - point1.long, 2));
        distance = distance*0.05;
    }
    return distance;
};

export const getClosestStop = (robotPosition: GPSPoint, stops: StopData[] | undefined,indoor:boolean) => {
    if (!stops || stops.length === 0) return null;
    return stops.reduce((closest, stop) => {
        const stopPosition: GPSPoint = { lat: stop.gps[0], long: stop.gps[1] };
        return !closest || getDistance(robotPosition, stopPosition,indoor) < getDistance(robotPosition, { lat: closest.gps[0], long: closest.gps[1] },indoor)
            ? stop
            : closest;
    }, stops[0]);
};

export const useNearestBusStop = (threshold: number = 0.002) => {
    const fetchStopData = useFetch<StopData[]>('/api/missions/get_stops_data', options);
    const stopDataRef = useRef<StopData[]>([]);
    const [nearestStop, setNearestStop] = useState<StopData | null>(null);
    const robotGPS = useRobotGPS();
    const robotTF = useRobotTF();
    const {robotDetails} = useRobotDetails();
    

    useEffect(() => {
        fetchStopData.get();
        return () => {
            fetchStopData.abortRequest && fetchStopData.abortRequest();
        };
    }, []);

    useEffect(() => {
        const { state: { status, data } } = fetchStopData;
        if (status === 'fetched' && data && data.length > 0) {
            stopDataRef.current = data;
        }
    }, [fetchStopData.state]);

    useEffect(() => {
        if ((!robotGPS && !robotTF) || stopDataRef.current.length === 0) return;

        let robotPosition: GPSPoint;
        if (robotDetails.isIndoor && robotTF){
            robotPosition = { lat: robotTF.lat, long: robotTF.long }
        }else if (robotGPS){
            robotPosition = { lat: robotGPS.lat, long: robotGPS.long }
        }else{
            return
        }
        const closestStop = getClosestStop(robotPosition, stopDataRef.current,robotDetails.isIndoor);

        if (closestStop) {
            const stopPosition: GPSPoint = { lat: closestStop.gps[0], long: closestStop.gps[1] };
            const distanceToStop = getDistance(robotPosition, stopPosition,robotDetails.isIndoor);
            setNearestStop(distanceToStop <= threshold ? closestStop : null);
        } else {
            setNearestStop(null);
        }
    }, [robotGPS,robotTF, stopDataRef.current]);

    return nearestStop;
};
